import { useEffect, useState } from 'react';

// HOW LONG YOU HAVE BEEN WAITING, for the buttons that go and ask a model something.
//
// This lived inside RepairAskModal with a comment saying it was deliberately not shared:
// everything else in this app that shows a duration shows the minutes and seconds of a
// video, and this is a wait, which is a different thing wearing the same format. That
// reasoning was right and only covered one caller. There are two now — a repair proposal
// and the search for a part in the listing photographs — and a second copy of a ticker is
// where one of them quietly stops matching.
//
// A wait is still not a video: nothing here belongs beside formatTime, which reads a
// manual's own printed durations.

// Seconds as m:ss.
export const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

// Seconds since `busy` last became true, and zero while it is not.
//
// EVERY HALF SECOND rather than every second: a clock ticked on the same period it
// displays drifts visibly against itself — you watch it sit on 3 for a moment and then
// jump to 5 — because the interval and the second boundary are not aligned and never will
// be. Twice the rate costs nothing and keeps every number on screen for about as long as
// every other.
//
// Counted from a timestamp and not by adding one each tick: a background tab throttles
// intervals to once a minute, and a counter would come back believing four seconds had
// passed during a two-minute wait.
export function useWaited(busy: boolean): number {
  const [waited, setWaited] = useState(0);
  useEffect(() => {
    if (!busy) return;
    setWaited(0);
    const started = Date.now();
    const tick = setInterval(() => setWaited(Math.round((Date.now() - started) / 1000)), 500);
    return () => clearInterval(tick);
  }, [busy]);
  return waited;
}

// Seconds since a moment the CALLER owns — a timestamp it keeps somewhere the wait itself
// lives — and 0 when there is nothing running.
//
// useWaited above starts its count when the component first sees `busy`, which is the right
// answer for a wait that begins and ends under one screen. Phase 4's does not: it walks a
// dozen steps one call at a time, minutes at a stretch, and a reader who leaves the tab and
// comes back mounts a fresh hook — which would start again at zero and call a three-minute
// run twelve seconds. Timing from a start the RUN holds (in its store, with the rest of its
// state) means whoever is on screen, whenever they arrive, reads the same number.
//
// Ticked every half second and computed from the timestamp, for the two reasons useWaited
// gives: a clock ticked on the period it displays visibly drifts against itself, and a
// counter that adds one per tick believes a throttled background tab was four seconds long.
export function useSince(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt == null) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(tick);
  }, [startedAt]);
  return startedAt == null ? 0 : Math.max(0, Math.round((now - startedAt) / 1000));
}
